import { Link } from 'react-router-dom';

import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';
import { ACCOUNTING_EMPTY } from './empty';
import { MoneyCell, commitsSub } from './MoneyCell';
import {
  TREATMENT_NODE_LABELS,
  fmtPct,
  rollupTreatmentByEngineer,
  type TreatmentRow,
} from './rollups';

const COLS: Array<{ key: TreatmentRow }> = [
  { key: 'opex_rd' },
  { key: 'opex_other' },
  { key: 'capex' },
];

/**
 * Treatment × Engineer. Engineer rows (humans and agents — agents marked
 * 🤖), accounting-treatment columns. Sibling of TeamsView for the "by team"
 * + "treatment" selection; rows are sorted by total cost descending.
 */
export function TreatmentByEngineerView() {
  const { data, isPending, error } = useAccountingView();
  if (isPending) return <Skeleton title='Treatment × Engineer' rows={5} />;
  if (error) return <div className={styles.errorBox}>Failed to load accounting view: {error.message}</div>;
  if (!data) return null;
  if (data.commits.length === 0) return <EmptyState {...ACCOUNTING_EMPTY} />;

  const r = rollupTreatmentByEngineer(data);

  const engineers = [...data.engineers].sort((a, b) => {
    const at = r.engineerTotals[a.id]?.cost_usd ?? 0;
    const bt = r.engineerTotals[b.id]?.cost_usd ?? 0;
    return bt - at;
  });

  return (
    <table className={styles.matrix}>
      <thead>
        <tr>
          <th>Engineer</th>
          {COLS.map(({ key }) => (
            <th key={key}>{TREATMENT_NODE_LABELS[key]}</th>
          ))}
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {engineers.map((e) => {
          const rowTotal = r.engineerTotals[e.id];
          return (
            <tr key={e.id}>
              <td>
                <Link
                  to={`/accounting/engineer/${encodeURIComponent(e.id)}`}
                  className={styles.linkPlain}
                >
                  {e.display_name}
                  {e.kind === 'agent' ? ' 🤖' : ''}
                </Link>
              </td>
              {COLS.map(({ key }) => {
                const cell = r.cells[`${key}|${e.id}`];
                return (
                  <td key={key}>
                    <MoneyCell cost={cell?.cost_usd ?? 0} sub={commitsSub(cell?.count ?? 0)} />
                  </td>
                );
              })}
              <td>
                <MoneyCell
                  cost={rowTotal?.cost_usd ?? 0}
                  sub={`${fmtPct(rowTotal?.cost_usd ?? 0, r.grandTotal.cost_usd)} of total`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          {COLS.map(({ key }) => {
            const colTotal = r.treatmentTotals[key];
            return (
              <td key={key}>
                <MoneyCell cost={colTotal.cost_usd} sub={commitsSub(colTotal.count)} />
              </td>
            );
          })}
          <td>
            <MoneyCell cost={r.grandTotal.cost_usd} sub={commitsSub(r.grandTotal.count)} />
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
